import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
import { generateFAQStructuredData } from '../utils/structuredData';

export function VanligaFragor() {
  // FAQ data for the site
  const faqData = [
    {
      question: "Vad är SOKASTIFTELSEMEDEL.SE?",
      answer: "SOKASTIFTELSEMEDEL.SE är en söktjänst som hjälper dig hitta stiftelser i Sverige som passar ditt ändamål. Vi samlar information från över 17 000 stiftelser för att göra din sökprocess enklare och effektivare."
    },
    {
      question: "Är det gratis att använda tjänsten?",
      answer: "Grundläggande sökning är kostnadsfri. Vi erbjuder även premiumtjänster med avancerade sökfunktioner, AI-stöd och specialverktyg för att maximera dina chanser att hitta rätt stiftelser."
    },
    {
      question: "Hur aktuell är informationen?",
      answer: "Vi uppdaterar regelbundet vår databas med information från officiella källor. Dock rekommenderar vi alltid att du verifierar informationen direkt med stiftelsen innan du skickar in en ansökan."
    },
    {
      question: "Kan jag söka stiftelsemedel via er webbplats?",
      answer: "Nej, vi förmedlar endast information om stiftelser. Ansökningar måste skickas direkt till respektive stiftelse enligt deras specifika ansökningsprocess."
    },
    {
      question: "Hur hittar jag stiftelser som passar mig?",
      answer: "Använd vår sökfunktion och ange nyckelord kopplade till ditt ändamål eller din situation. Du kan också filtrera resultat baserat på region, belopp och andra kriterier för att hitta de mest relevanta stiftelserna."
    },
    {
      question: "Vad är skillnaden mellan gratis- och premiumversionen?",
      answer: "Gratisversionen ger dig tillgång till grundläggande sökfunktioner och basinformation om stiftelser. Premiumversionen erbjuder avancerade sökverktyg, AI-stöd, djupanalys av stiftelser, kontaktinformation, ansökningstider och specialverktyg för att maximera dina chanser att få stöd."
    },
    {
      question: "Hur ofta uppdateras informationen om ansökningsperioder?",
      answer: "Vi strävar efter att uppdatera information om ansökningsperioder så snart ny information blir tillgänglig. För premiumanvändare erbjuder vi även påminnelser om kommande deadlines för stiftelser du är intresserad av."
    },
    {
      question: "Kan jag spara stiftelser jag är intresserad av?",
      answer: "Ja, du kan spara stiftelser i din personliga lista för att enkelt hitta dem senare. Premiumanvändare får tillgång till utökade funktioner som anteckningar, statusuppföljning och exportmöjligheter."
    }
  ];

  // Generate structured data for FAQ
  const faqStructuredData = generateFAQStructuredData(faqData);
  
  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <SEO
        title="Vanliga frågor om SOKASTIFTELSEMEDEL.SE"
        description="Hitta svar på vanliga frågor om hur du använder SOKASTIFTELSEMEDEL.SE för att hitta stiftelser som passar ditt ändamål."
        keywords="vanliga frågor, FAQ, stiftelser, söka stiftelsemedel, stiftelsekapital, hjälp, support"
        canonicalUrl="https://www.sokastiftelsemedel.se/vanliga-fragor"
        structuredData={{
          faq: faqStructuredData
        }}
      />
      
      <article className="bg-white rounded-lg shadow-md p-6 mb-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-[#2563eb] mb-2">Vanliga frågor (FAQ)</h1>
          <p className="text-gray-600">
            Här hittar du svar på de vanligaste frågorna om SOKASTIFTELSEMEDEL.SE och hur du kan använda vår tjänst för att hitta stiftelser som passar ditt ändamål.
          </p>
        </header>
        
        <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
          {faqData.map((faq, index) => (
            <div 
              key={index} 
              className="border-b border-gray-200 pb-5 last:border-0"
              itemScope 
              itemProp="mainEntity" 
              itemType="https://schema.org/Question"
            >
              <h2 className="text-xl font-semibold text-[#A3C1DA] flex items-center gap-2" itemProp="name">
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                {faq.question}
              </h2>
              <div 
                className="mt-2 text-gray-700"
                itemScope 
                itemProp="acceptedAnswer" 
                itemType="https://schema.org/Answer"
              >
                <div itemProp="text">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-8 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">Senast uppdaterad: 3 april 2025</p>
          <div className="mt-4">
            <Link to="/" className="text-[#2563eb] hover:underline flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Tillbaka till startsidan
            </Link>
          </div>
        </div>
      </article>
      
      <div className="bg-[#F2F7FF] p-4 rounded-lg border border-[#A3C1DA] mb-8">
        <h2 className="text-lg font-semibold mb-2 text-[#2563eb]">Behöver du mer hjälp?</h2>
        <p className="mb-3">
          Om du inte hittar svaret på din fråga här, är du välkommen att kontakta oss direkt.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/kontakta-oss" className="bg-[#A3C1DA] text-white px-4 py-2 rounded hover:bg-[#7A98B0] transition-colors text-center">
            Kontakta oss
          </Link>
          <Link to="/premium" className="bg-[#F2C1D1] text-white px-4 py-2 rounded hover:bg-[#D1A3B3] transition-colors text-center">
            Utforska våra premiumtjänster
          </Link>
        </div>
      </div>
    </main>
  );
}